import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  Facebook,
  FileText,
  Globe2,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Search,
  ShieldCheck,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react'
import { CRM_CONFIG } from '../../config'
import type { CrmDataset, Lead } from '../../types/crm'
import {
  recordDashboardActivity,
} from '../dashboard/dashboardActivity'
import { getLeadDisplayName } from '../leads/leadDisplay'
import { LeadWebsiteLogo } from '../leads/LeadWebsiteLogo'
import {
  getEnteredUpdates,
  getMissingInformationFields,
  validateMissingInformationUpdates,
  type MissingInformationField,
  type MissingInformationFieldKey,
  type MissingInformationUpdates,
} from './missingInformation'
import {
  getWritebackStatus,
  submitMissingInformationUpdateWithUndo,
  WritebackError,
  type WritebackStatus,
} from './writebackClient'

export type DashboardReviewQueue =
  | 'missing-owner'
  | 'missing-contact'
  | 'missing-website'
  | 'missing-location'
  | 'missing-notes'

const PRIORITY_ORDER = new Map<string, number>([
  ['A+', 0],
  ['A', 1],
  ['B', 2],
  ['C', 3],
  ['D', 4],
])

const QUEUE_TITLES: Record<DashboardReviewQueue, string> = {
  'missing-owner': 'Missing Owners',
  'missing-contact': 'Missing Contact Information',
  'missing-website': 'Missing Website',
  'missing-location': 'Missing Location',
  'missing-notes': 'Missing Notes',
}

function queueQuestion(queue: DashboardReviewQueue): string {
  switch (queue) {
    case 'missing-owner':
      return 'Have you found the owners of these companies,'
    case 'missing-contact':
      return 'Have you already found their email and phone,'
    case 'missing-website':
      return 'Have you already found their websites,'
    case 'missing-location':
      return 'Do you already know their city and state,'
    case 'missing-notes':
      return 'Do you already have useful notes for these companies,'
  }
}

function locationName(lead: Lead): string {
  return [
    lead.organization?.city,
    lead.organization?.state,
  ]
    .filter(Boolean)
    .join(', ')
}

function priorityRank(lead: Lead): number {
  const priority =
    lead.priority ||
    lead.organization?.priority ||
    lead.opportunity?.priority ||
    ''

  return (
    PRIORITY_ORDER.get(
      priority.trim().toUpperCase(),
    ) ?? 99
  )
}

function matchesQueue(
  lead: Lead,
  queue: DashboardReviewQueue,
): boolean {
  const fields = getMissingInformationFields(lead)
  const keys = new Set(
    fields.map((field) => field.key),
  )

  if (queue === 'missing-owner') {
    return !lead.ownerIdentified
  }

  if (queue === 'missing-contact') {
    return keys.has('email') && keys.has('phone')
  }

  if (queue === 'missing-website') {
    return keys.has('website')
  }

  if (queue === 'missing-location') {
    return keys.has('city') || keys.has('state')
  }

  return keys.has('notes')
}

function getQueueLeads(
  leads: Lead[],
  queue: DashboardReviewQueue,
): Lead[] {
  return [...leads]
    .filter((lead) => matchesQueue(lead, queue))
    .sort((a, b) => {
      const priorityDifference =
        priorityRank(a) - priorityRank(b)

      if (priorityDifference !== 0) {
        return priorityDifference
      }

      return getLeadDisplayName(a).localeCompare(
        getLeadDisplayName(b),
      )
    })
}

function fieldsForQueue(
  lead: Lead,
  queue: DashboardReviewQueue,
): MissingInformationField[] {
  const missingFields =
    getMissingInformationFields(lead)

  if (queue === 'missing-owner') {
    return [
      {
        key: 'owner',
        label: 'Owner name',
      },
    ]
  }

  if (queue === 'missing-contact') {
    return missingFields.filter(
      (field) =>
        field.key === 'email' ||
        field.key === 'phone',
    )
  }

  if (queue === 'missing-website') {
    return missingFields.filter(
      (field) => field.key === 'website',
    )
  }

  if (queue === 'missing-location') {
    return missingFields.filter(
      (field) =>
        field.key === 'city' ||
        field.key === 'state',
    )
  }

  return missingFields.filter(
    (field) => field.key === 'notes',
  )
}

function saveButtonLabel(
  queue: DashboardReviewQueue,
): string {
  if (queue === 'missing-owner') return 'Save Owner'
  if (queue === 'missing-contact') {
    return 'Save Contact Information'
  }
  if (queue === 'missing-website') {
    return 'Save Website'
  }
  if (queue === 'missing-location') {
    return 'Save Location'
  }
  return 'Save Notes'
}

function activityLabel(
  queue: DashboardReviewQueue,
): string {
  if (queue === 'missing-owner') return 'Owner added'
  if (queue === 'missing-contact') {
    return 'Contact info added'
  }
  if (queue === 'missing-website') {
    return 'Website added'
  }
  if (queue === 'missing-location') {
    return 'Location updated'
  }
  return 'Notes added'
}

const EMPTY_RESEARCH_VALUES = new Set([
  '',
  'na',
  'n/a',
  'not provided',
  'not identified',
  'none',
])

function researchValue(value: unknown): string {
  const text = String(value ?? '').trim()

  if (
    !text ||
    EMPTY_RESEARCH_VALUES.has(text.toLowerCase())
  ) {
    return 'NA'
  }

  return text
}

function researchOwnerName(lead: Lead): string {
  if (!lead.ownerIdentified) return 'NA'

  return researchValue(
    lead.owner || lead.opportunity?.name,
  )
}

function researchNotes(lead: Lead): string {
  return researchValue(
    lead.opportunity?.notes ||
      lead.organization?.notes,
  )
}

function matchesQueueSearch(
  lead: Lead,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase()

  if (!needle) return true

  const searchable = [
    getLeadDisplayName(lead),
    lead.owner,
    lead.opportunity?.name,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return searchable.includes(needle)
}

function ResearchInformationItem({
  Icon,
  label,
  value,
  wide = false,
}: {
  Icon: LucideIcon
  label: string
  value: string
  wide?: boolean
}) {
  const missing = value === 'NA'

  return (
    <div
      className={`dashboard-research-item${
        wide ? ' is-wide' : ''
      }${missing ? ' is-missing' : ''}`}
    >
      <Icon aria-hidden="true" size={17} />

      <div>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  )
}
export function DashboardActionQueueModal({
  dataset,
  queue,
  onClose,
  onUpdated,
}: {
  dataset: CrmDataset
  queue: DashboardReviewQueue
  onClose: () => void
  onUpdated: () => void
}) {
  const [selectedLeadId, setSelectedLeadId] =
    useState<string | null>(null)

  const [searchQuery, setSearchQuery] =
    useState('')

  const [completedIds, setCompletedIds] =
    useState<Set<string>>(() => new Set())

  const [values, setValues] =
    useState<MissingInformationUpdates>({})

  const [errors, setErrors] =
    useState<
      Partial<
        Record<
          MissingInformationFieldKey,
          string
        >
      >
    >({})

  const [formError, setFormError] = useState('')
  const [submitting, setSubmitting] =
    useState(false)

  const [saveSucceeded, setSaveSucceeded] =
    useState(false)

  const [writebackStatus, setWritebackStatus] =
    useState<WritebackStatus | null>(null)
const [isClosing, setIsClosing] =
    useState(false)

  const modalRef = useRef<HTMLElement>(null)
  const closeTimerRef =
    useRef<number | null>(null)

  const closingRef = useRef(false)

  const queueLeads = useMemo(
    () => getQueueLeads(dataset.leads, queue),
    [dataset.leads, queue],
  )

  const remainingLeads = useMemo(
    () =>
      queueLeads.filter(
        (lead) => !completedIds.has(lead.id),
      ),
    [queueLeads, completedIds],
  )

  const filteredRemainingLeads = useMemo(
    () =>
      remainingLeads.filter((lead) =>
        matchesQueueSearch(lead, searchQuery),
      ),
    [remainingLeads, searchQuery],
  )

  const selectedLead =
    remainingLeads.find(
      (lead) => lead.id === selectedLeadId,
    ) ?? null

  const fields = useMemo(
    () =>
      selectedLead
        ? fieldsForQueue(selectedLead, queue)
        : [],
    [selectedLead, queue],
  )

  const enteredUpdates = useMemo(
    () => getEnteredUpdates(fields, values),
    [fields, values],
  )

  const title = QUEUE_TITLES[queue]

  const beginClose = useCallback(() => {
    if (closingRef.current) return

    closingRef.current = true
    setIsClosing(true)

    const reducedMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches

    closeTimerRef.current =
      window.setTimeout(
        onClose,
        reducedMotion ? 0 : 180,
      )
  }, [onClose])

  useEffect(() => {
    let active = true

    getWritebackStatus().then((status) => {
      if (active) {
        setWritebackStatus(status)
      }
    })

    return () => {
      active = false
    }
  }, [])
useEffect(() => {
    const appShell =
      document.querySelector<HTMLElement>(
        '.app-shell',
      )

    const previousOverflow =
      document.body.style.overflow

    appShell?.classList.add(
      'is-lead-modal-open',
    )

    document.body.style.overflow = 'hidden'

    function handleKeyDown(
      event: KeyboardEvent,
    ) {
      if (event.key !== 'Escape') return

      event.preventDefault()

      if (selectedLeadId) {
        setSelectedLeadId(null)
        setValues({})
        setErrors({})
        setFormError('')
      } else {
        beginClose()
      }
    }

    window.addEventListener(
      'keydown',
      handleKeyDown,
    )

    return () => {
      appShell?.classList.remove(
        'is-lead-modal-open',
      )

      document.body.style.overflow =
        previousOverflow

      window.removeEventListener(
        'keydown',
        handleKeyDown,
      )

      if (closeTimerRef.current !== null) {
        window.clearTimeout(
          closeTimerRef.current,
        )
      }
    }
  }, [beginClose, selectedLeadId])

  function chooseLead(lead: Lead) {
    setSelectedLeadId(lead.id)
    setValues({})
    setErrors({})
    setFormError('')

    window.requestAnimationFrame(() => {
      modalRef.current
        ?.querySelector<HTMLElement>(
          'input, textarea',
        )
        ?.focus()
    })
  }

  function returnToList() {
    setSelectedLeadId(null)
    setValues({})
    setErrors({})
    setFormError('')
  }

  async function saveSelected(
    event: FormEvent<HTMLFormElement>,
  ) {
    event.preventDefault()

    if (!selectedLead) return

    const nextErrors =
      validateMissingInformationUpdates(
        fields,
        enteredUpdates,
      )

    for (const field of fields) {
      if (!enteredUpdates[field.key]) {
        nextErrors[field.key] =
          'Complete this field before saving.'
      }
    }

    setErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0) {
      setFormError(
        'Complete the required information before saving.',
      )
      return
    }

    if (
      !writebackStatus?.configured ||
      !writebackStatus.authenticated
    ) {
      setFormError(
        'Secure Google Sheets write-back is not ready.',
      )
      return
    }

    setSaveSucceeded(false)
    setSubmitting(true)
    setFormError('')

    try {
      const result =
        await submitMissingInformationUpdateWithUndo({
          spreadsheetId: CRM_CONFIG.sheetId,
          organizationRow:
            selectedLead.organization?.sheetRow ??
            null,
          opportunityRow:
            selectedLead.opportunity?.sheetRow ??
            null,
          updates: enteredUpdates,
        })

      setSaveSucceeded(true)

      await new Promise<void>((resolve) =>

        window.setTimeout(resolve, 650),

      )


      const savedName =
        getLeadDisplayName(selectedLead)

      recordDashboardActivity({
          leadId: selectedLead.id,
          company: savedName,
          action: activityLabel(queue),
          fieldsResolved:
            Object.keys(enteredUpdates).length,
          undoToken: result.undoToken,
        })
setCompletedIds((current) => {
        const next = new Set(current)
        next.add(selectedLead.id)
        return next
      })

      setSelectedLeadId(null)
      setValues({})
      setErrors({})

      onUpdated()
    } catch (cause) {
      if (
        cause instanceof WritebackError &&
        cause.code === 'CONFLICT'
      ) {
        setFormError(
          `The Sheet changed before this update. Nothing was overwritten. Conflicting fields: ${cause.conflicts.join(', ')}.`,
        )
      } else {
        setFormError(
          cause instanceof Error
            ? cause.message
            : 'The spreadsheet update could not be completed.',
        )
      }
    } finally {
      setSubmitting(false)
      setSaveSucceeded(false)
    }
  }

  return createPortal(
    <div
      className={`lead-modal-layer${
        isClosing ? ' is-closing' : ''
      }`}
      data-testid="dashboard-action-queue-modal"
    >
      <button
        aria-label={`Close ${title}`}
        className="lead-modal-backdrop"
        onClick={beginClose}
        type="button"
      />

      <section
        aria-labelledby="dashboard-queue-title"
        aria-modal="true"
        className="lead-modal review-modal dashboard-queue-modal"
        ref={modalRef}
        role="dialog"
      >
        <header className="review-modal-header">
          <div className="review-modal-heading">
            <span>Dashboard action</span>

            <h2 id="dashboard-queue-title">
              {title}
            </h2>

            <p className="dashboard-action-question">
              {queueQuestion(queue)}{' '}
              <span className="attention-name">
                William
              </span>
              ?
            </p>
          </div>

          <button
            aria-label={`Close ${title}`}
            className="icon-button lead-modal-close"
            onClick={beginClose}
            title="Close"
            type="button"
          >
            <X size={21} />
          </button>
        </header>

        <div className="lead-modal-content review-modal-content">
          {!selectedLead ? (
            remainingLeads.length > 0 ? (
              <section className="dashboard-queue-picker">
                <label
                  className="dashboard-queue-search"
                  htmlFor="dashboard-queue-search"
                >
                  <Search
                    aria-hidden="true"
                    size={18}
                  />

                  <input
                    autoComplete="off"
                    className="text-input"
                    id="dashboard-queue-search"
                    onChange={(event) =>
                      setSearchQuery(
                        event.target.value,
                      )
                    }
                    placeholder="Search company or owner..."
                    type="search"
                    value={searchQuery}
                  />
                </label>

                {filteredRemainingLeads.length > 0 ? (
                  <div className="dashboard-action-list">
                    {filteredRemainingLeads.map((lead) => {
                      const company =
                        getLeadDisplayName(lead)

                      const note =
                        lead.opportunity?.notes ||
                        lead.organization?.notes ||
                        ''

                      return (
                        <button
                          className="dashboard-action-row"
                          key={lead.id}
                          onClick={() =>
                            chooseLead(lead)
                          }
                          type="button"
                        >
                          <div className="dashboard-action-logo">
                            <LeadWebsiteLogo
                              website={
                                lead.organization
                                  ?.website ?? ''
                              }
                            />
                          </div>

                          <div className="dashboard-action-copy">
                            <strong>
                              {company}
                            </strong>

                            <span className="dashboard-action-status">
                              {queue ===
                              'missing-owner'
                                ? 'Owner not provided'
                                : queue ===
                                    'missing-contact'
                                  ? 'Email and phone not provided'
                                  : queue ===
                                      'missing-website'
                                    ? 'Website not provided'
                                    : queue ===
                                        'missing-location'
                                      ? 'Location incomplete'
                                      : 'Notes not provided'}
                            </span>

                            {note ? (
                              <p>{note}</p>
                            ) : null}
                          </div>

                          <ChevronRight
                            aria-hidden="true"
                            className="dashboard-action-chevron"
                            size={20}
                          />
                        </button>
                      )
                    })}
                  </div>
                ) : (
                  <div className="dashboard-queue-search-empty">
                    <Search
                      aria-hidden="true"
                      size={21}
                    />
                    <strong>
                      No matching prospects
                    </strong>
                    <span>
                      Search by company or owner name.
                    </span>
                  </div>
                )}
              </section>
            ) : (
              <section className="review-success">
                <span>
                  <CheckCircle2 size={26} />
                </span>

                <h3>{title} complete</h3>

                <p>
                  There are no remaining
                  prospects that need this action.
                </p>

                <div className="review-actions">
                  <button
                    className="button button-primary"
                    onClick={beginClose}
                    type="button"
                  >
                    Done
                  </button>
                </div>
              </section>
            )
          ) : (
            <section className="review-step">
              <button
                className="review-back"
                onClick={returnToList}
                type="button"
              >
                <ArrowLeft size={16} />
                Back to organizations
              </button>

              <div className="review-step-heading">
                <i>1</i>

                <div>
                  <h3>
                    {getLeadDisplayName(
                      selectedLead,
                    )}
                  </h3>

                  <p>
                    Complete only the
                    information required for
                    this action.
                  </p>
                </div>
              </div>

              <div className="dashboard-research-card">
                <div className="dashboard-research-heading">
                  <div>
                    <span>Prospect information</span>
                    <strong>
                      What we currently know
                    </strong>
                  </div>

                  <small>
                    NA marks information that is
                    still missing.
                  </small>
                </div>

                <div className="dashboard-research-grid">
                  <ResearchInformationItem
                    Icon={Building2}
                    label="Company"
                    value={researchValue(
                      getLeadDisplayName(
                        selectedLead,
                      ),
                    )}
                  />

                  <ResearchInformationItem
                    Icon={UserRound}
                    label="Owner"
                    value={researchOwnerName(
                      selectedLead,
                    )}
                  />

                  <ResearchInformationItem
                    Icon={Globe2}
                    label="Website"
                    value={researchValue(
                      selectedLead.organization
                        ?.website,
                    )}
                  />

                  <ResearchInformationItem
                    Icon={Facebook}
                    label="Facebook"
                    value={researchValue(
                      selectedLead.organization
                        ?.facebook,
                    )}
                  />

                  <ResearchInformationItem
                    Icon={Linkedin}
                    label="LinkedIn"
                    value={researchValue(
                      selectedLead.organization
                        ?.linkedin,
                    )}
                  />

                  <ResearchInformationItem
                    Icon={Mail}
                    label="Email"
                    value={researchValue(
                      selectedLead.organization
                        ?.email,
                    )}
                  />

                  <ResearchInformationItem
                    Icon={Phone}
                    label="Phone"
                    value={researchValue(
                      selectedLead.organization
                        ?.phone,
                    )}
                  />

                  <ResearchInformationItem
                    Icon={MapPin}
                    label="Location"
                    value={researchValue(
                      locationName(selectedLead),
                    )}
                  />

                  <ResearchInformationItem
                    Icon={FileText}
                    label="Notes"
                    value={researchNotes(
                      selectedLead,
                    )}
                    wide
                  />
                </div>
              </div>

              <div className="dashboard-missing-heading">
                <span>Missing information</span>
                <p>
                  Only the fields required for this
                  action can be updated.
                </p>
              </div>

              <form
                className="review-fields"
                noValidate
                onSubmit={saveSelected}
              >
                <div className="review-field-grid">
                  {fields.map((field) => {
                    const errorId =
                      `queue-${field.key}-error`

                    const shared = {
                      'aria-describedby':
                        errors[field.key]
                          ? errorId
                          : undefined,
                      'aria-invalid':
                        Boolean(
                          errors[field.key],
                        ),
                      id: `queue-${field.key}`,
                      name: field.key,
                      onChange: (
                        event: ChangeEvent<
                          HTMLInputElement |
                          HTMLTextAreaElement
                        >,
                      ) => {
                        setValues((current) => ({
                          ...current,
                          [field.key]:
                            event.target.value,
                        }))

                        setErrors((current) => ({
                          ...current,
                          [field.key]:
                            undefined,
                        }))
                      },
                      value:
                        values[field.key] ?? '',
                    }

                    return (
                      <label
                        className={`review-field${
                          field.multiline
                            ? ' is-wide'
                            : ''
                        }`}
                        htmlFor={`queue-${field.key}`}
                        key={field.key}
                      >
                        <span>
                          {field.label} *
                        </span>

                        {field.multiline ? (
                          <textarea
                            className="text-input"
                            rows={4}
                            {...shared}
                          />
                        ) : (
                          <input
                            className="text-input"
                            inputMode={
                              field.inputMode
                            }
                            type={
                              field.inputMode ===
                              'email'
                                ? 'email'
                                : field.inputMode ===
                                    'url'
                                  ? 'url'
                                  : 'text'
                            }
                            {...shared}
                          />
                        )}

                        {errors[field.key] ? (
                          <small
                            className="review-inline-error"
                            id={errorId}
                          >
                            {
                              errors[
                                field.key
                              ]
                            }
                          </small>
                        ) : null}
                      </label>
                    )
                  })}
                </div>

                {!writebackStatus ? (
                  <div className="review-status">
                    Checking secure write-back
                    configuration...
                  </div>
                ) : null}

                {writebackStatus &&
                !writebackStatus.configured ? (
                  <div className="review-status is-warning">
                    <CircleAlert size={18} />
                    <span>
                      <strong>
                        Write-back is not
                        configured.
                      </strong>
                    </span>
                  </div>
                ) : null}

                {writebackStatus?.configured &&
                !writebackStatus.authenticated ? (
                  <div className="review-status is-warning">
                    <CircleAlert size={18} />
                    <span>
                      <strong>
                        A secure server session
                        is required.
                      </strong>
                    </span>
                  </div>
                ) : null}

                {formError ? (
                  <p
                    className="review-form-error"
                    role="alert"
                  >
                    {formError}
                  </p>
                ) : null}

                <div className="review-actions">
                  <button
                    className="button button-primary"
                    disabled={
                      submitting ||
                      !writebackStatus?.configured ||
                      !writebackStatus.authenticated
                    }
                    type="submit"
                  >
                    {submitting ? (
                        <span
                          aria-label={saveSucceeded ? 'Saved' : 'Saving'}
                          className={`wf-action-state ${
                            saveSucceeded
                              ? 'is-success'
                              : 'is-loading'
                          }`}
                        >
                          <span
                            aria-hidden="true"
                            className="wf-action-state__rail"
                          >
                            <span />
                          </span>

                          <span
                            aria-hidden="true"
                            className="wf-action-state__check"
                          />
                        </span>
                      ) : saveButtonLabel(queue)}
                  </button>
                </div>
              </form>
            </section>
          )}
        </div>
<footer className="lead-modal-footer review-modal-footer">
          <ShieldCheck size={14} />
          Only the selected missing
          information can be updated.
        </footer>
      </section>
    </div>,
    document.body,
  )
}



